import { useMemo, useRef, useState } from "react"
import { ColumnDef, SortingState } from "@tanstack/react-table"
import DataTable from "Components/molecule/data-table"
import TextInput from "Components/molecule/inputs/text-input"
import ColumnVisibilityDropdown from "Components/molecule/column-visibility-dropdown"
import { ExportButton, PrintButton } from "Components/atoms/buttons/report-buttons"
import useDataTable from "hooks/useDataTable"
import { openReportView } from "utils/report-snapshot"

interface EmployeeTableProps<T> {
    title: string
    // Page name for the export / print view
    report: string
    rows: T[]
    columns: ColumnDef<T, any>[]
    // Text the search box matches against (lower-cased)
    searchText: (row: T) => string
    initialSorting?: SortingState
}

// The detail table at the bottom of each HR report: the rows left by the page filters, with a
// search box and the standard Columns / Export / Print buttons. No inner scroll; the page scrolls.
const EmployeeTable = <T,>({ title, report, rows, columns, searchText, initialSorting }: EmployeeTableProps<T>) => {
    const [search, setSearch] = useState("")
    const contentRef = useRef<HTMLDivElement>(null)

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase()
        return q ? rows.filter(r => searchText(r).toLowerCase().includes(q)) : rows
    }, [rows, search, searchText])

    const table = useDataTable<T>({ data: visible, columns, initialSorting })

    const openView = (kind: "print" | "export") => openReportView(kind, {
        title: `${report} - ${title}`,
        module: "Human Resources",
        content: contentRef.current,
    })

    return (
        <div className="section-container hr-report-card">
            <header className="hr-report-card-header d-print-none">
                <h5>{title} <span className="hr-report-card-hint">({visible.length.toLocaleString("en-CA")} {visible.length === 1 ? "row" : "rows"})</span></h5>
                <div className="d-flex flex-row flex-wrap align-items-center gap-2">
                    <TextInput
                        className="hr-report-search"
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        aria-label={`Search ${title}`}
                    />
                    <ColumnVisibilityDropdown table={table} />
                    <ExportButton onClick={() => openView("export")} />
                    <PrintButton onClick={() => openView("print")} />
                </div>
            </header>
            <div ref={contentRef} style={{ overflowX: "auto" }}>
                <DataTable table={table} columns={columns} stickyHead />
            </div>
        </div>
    )
}

export default EmployeeTable
